import React, { useEffect, useRef, useState } from "react";
import { API, apiErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Mic, MicOff, Send, Camera, Calculator as CalcIcon, Volume2, StopCircle, Loader2 } from "lucide-react";
import Markdown from "../../components/Markdown";
import { createRecognizer, speak, stopSpeaking, SCI_SYMBOLS } from "../../lib/speech";

function Calculator({ onInsert }) {
  const [expr, setExpr] = useState("");
  const [res, setRes] = useState("");
  const btns = ["7","8","9","/","4","5","6","*","1","2","3","-","0",".","(",")","+","^","√","π"];
  const evaluate = () => {
    try {
      let e = expr.replace(/π/g, "Math.PI").replace(/√/g, "Math.sqrt").replace(/\^/g, "**");
      // eslint-disable-next-line no-new-func
      const v = Function(`"use strict";return (${e})`)();
      setRes(String(v));
    } catch { setRes("Error"); }
  };
  return (
    <div className="brut-card p-3 bg-white w-full">
      <div className="text-xs font-bold uppercase tracking-widest mb-2">Calculator</div>
      <input value={expr} onChange={(e) => setExpr(e.target.value)}
        className="brut-input w-full px-2 py-2 mb-2 font-mono" data-testid="calc-expr"/>
      <div className="text-right font-mono text-sm mb-2 h-5">{res && <>= {res}</>}</div>
      <div className="grid grid-cols-4 gap-1 mb-2">
        {btns.map((b) => (
          <button key={b} onClick={() => setExpr(expr + b)}
            className="brut-border brut-shadow-sm py-1.5 font-bold text-sm hover:bg-[#FFF5D1]" data-testid={`calc-${b}`}>
            {b}
          </button>
        ))}
      </div>
      <div className="flex gap-1">
        <Button onClick={() => { setExpr(""); setRes(""); }} className="brut-btn rounded-none flex-1 bg-white hover:bg-white text-black text-sm">Clear</Button>
        <Button onClick={evaluate} className="brut-btn rounded-none flex-1 bg-[#E9E6FA] text-black hover:bg-[#E9E6FA] text-sm">=</Button>
        <Button onClick={() => res && onInsert(`${expr} = ${res}`)}
          className="brut-btn rounded-none flex-1 bg-[#1A1A1A] text-white hover:bg-[#1A1A1A] text-sm">Insert</Button>
      </div>
    </div>
  );
}

export default function ChatTab({ cls, subject, chapter }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [showSymbols, setShowSymbols] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(-1);
  const [sessionId] = useState(() => `${cls}-${subject}-${chapter?.id || "gen"}-${Date.now()}`);
  const recRef = useRef(null);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  // Stop any TTS when leaving the chat tab / unmounting; revoke any image object URLs
  useEffect(() => {
    return () => {
      stopSpeaking();
      try { recRef.current?.stop(); } catch (e) { /* noop */ }
      setMessages((m) => {
        m.forEach((x) => { if (x.image?.startsWith("blob:")) URL.revokeObjectURL(x.image); });
        return m;
      });
    };
  }, []);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput(""); setBusy(true);
    // add placeholder assistant
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    try {
      const r = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: msg,
          context: chapter ? `Class ${cls} ${subject} — ${chapter.title}` : "",
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { role: "assistant", content: acc };
          return c;
        });
      }
    } catch (e) {
      toast.error(String(e.message || e));
      setMessages((m) => m.slice(0, -1));
    } finally { setBusy(false); }
  };

  const toggleMic = () => {
    if (listening) { recRef.current?.stop(); return; }
    const rec = createRecognizer(
      (text) => setInput((v) => (v ? v + " " : "") + text),
      () => setListening(false),
    );
    if (!rec) return toast.error("Voice input not supported in this browser");
    recRef.current = rec;
    rec.start(); setListening(true);
  };

  const handleImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB)");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    const userQuestion = input.trim();
    setBusy(true);
    setMessages((m) => [
      ...m,
      { role: "user", content: userQuestion || "Solve this question from the image", image: previewUrl },
      { role: "assistant", content: "" },
    ]);
    setInput("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("question", userQuestion);
      const r = await fetch(`${API}/solve-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      setMessages((m) => {
        const c = [...m]; c[c.length - 1] = { role: "assistant", content: data.answer }; return c;
      });
    } catch (e) {
      toast.error(String(e.message || e));
      setMessages((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const speakMsg = (i, text) => {
    if (speakingIdx === i) { stopSpeaking(); setSpeakingIdx(-1); return; }
    const ok = speak(text, {
      onStart: () => setSpeakingIdx(i),
      onEnd: () => setSpeakingIdx((cur) => (cur === i ? -1 : cur)),
      onError: (e) => {
        setSpeakingIdx((cur) => (cur === i ? -1 : cur));
        toast.error(`Read Aloud failed: ${e || "voice unavailable"}`);
      },
    });
    if (!ok) toast.error("Text-to-speech isn't supported on this browser.");
  };

  return (
    <div className="mt-4 grid lg:grid-cols-[1fr_320px] gap-4">
      <div className="brut-card bg-white p-4 flex flex-col h-[75vh]">
        <div className="flex-1 overflow-y-auto space-y-3 pr-2" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center text-neutral-600 py-10">
              <div className="text-lg font-bold">Ask anything about {chapter?.title || "this chapter"}</div>
              <div className="text-sm mt-1">Use the mic, type a question, or upload a photo of a problem.</div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] brut-border p-3 ${m.role === "user" ? "bg-[#FAF9F6]" : "bg-[#E9E6FA]"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold tracking-[0.2em] uppercase">
                    {m.role === "user" ? "You" : "AI Tutor"}
                  </span>
                  {m.role === "assistant" && m.content && (
                    <button onClick={() => speakMsg(i, m.content)} className="p-0.5" data-testid={`tts-msg-${i}`}>
                      {speakingIdx === i ? <StopCircle size={14} strokeWidth={2.5}/> : <Volume2 size={14} strokeWidth={2.5}/>}
                    </button>
                  )}
                </div>
                {m.role === "assistant" ? (
                  <Markdown>{m.content || "…"}</Markdown>
                ) : (
                  <div>
                    {m.image && (
                      <img
                        src={m.image}
                        alt="Uploaded question"
                        className="mb-2 max-h-56 w-auto brut-border rounded-none object-contain bg-white"
                        data-testid={`chat-user-image-${i}`}
                      />
                    )}
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && <div className="text-sm text-neutral-600 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Thinking…</div>}
          <div ref={endRef}/>
        </div>

        {showSymbols && (
          <div className="mt-2 brut-border p-2 bg-[#FFF5D1] flex flex-wrap gap-1">
            {SCI_SYMBOLS.map((s) => (
              <button key={s} onClick={() => setInput((v) => v + s)}
                className="px-2 py-1 brut-border brut-shadow-sm bg-white font-bold hover:bg-[#E9E6FA] text-sm" data-testid={`sym-${s}`}>
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="mt-2 flex items-end gap-2">
          <Textarea data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask a question, or press mic to speak…"
            className="brut-input rounded-none min-h-[52px] max-h-[120px]"/>
          <div className="flex flex-col gap-1">
            <Button onClick={toggleMic} data-testid="chat-mic-btn"
              className={`brut-btn rounded-none ${listening ? "bg-[#FFB3BA]" : "bg-white"} text-black hover:opacity-90`}>
              {listening ? <MicOff size={16} strokeWidth={2.5}/> : <Mic size={16} strokeWidth={2.5}/>}
            </Button>
            <Button onClick={() => fileRef.current?.click()} data-testid="chat-image-btn"
              title="Upload a photo of a question to solve"
              aria-label="Upload photo of question"
              className="brut-btn rounded-none bg-white text-black hover:bg-white">
              <Camera size={16} strokeWidth={2.5}/>
            </Button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => handleImage(e.target.files?.[0])} data-testid="chat-image-input"/>
          </div>
          <Button onClick={() => send()} disabled={busy || !input.trim()} data-testid="chat-send-btn"
            className="brut-btn rounded-none bg-[#1A1A1A] text-white hover:bg-[#1A1A1A] h-full">
            <Send size={16} strokeWidth={2.5}/>
          </Button>
        </div>

        <div className="mt-2 flex gap-2">
          <Button size="sm" onClick={() => setShowSymbols((s) => !s)} data-testid="toggle-symbols"
            className="brut-btn rounded-none bg-[#E9E6FA] text-black hover:bg-[#E9E6FA] text-xs">
            {showSymbols ? "Hide" : "Show"} Symbols
          </Button>
          <Button size="sm" onClick={() => setShowCalc((s) => !s)} data-testid="toggle-calc"
            className="brut-btn rounded-none bg-[#E8F3EC] text-black hover:bg-[#E8F3EC] text-xs lg:hidden">
            <CalcIcon size={14} className="mr-1" strokeWidth={2.5}/>Calculator
          </Button>
        </div>
      </div>

      <div className={`space-y-4 ${showCalc || "hidden lg:block"}`}>
        <Calculator onInsert={(s) => setInput((v) => (v ? v + " " : "") + s)}/>
        <div className="brut-card p-4 bg-[#FFF5D1] text-sm">
          <div className="font-bold mb-1">💡 Tips</div>
          <ul className="list-disc pl-4 space-y-1">
            <li>Press <b>Mic</b> to dictate a question.</li>
            <li>Upload a photo of a numerical to solve it.</li>
            <li>Insert symbols like α, β, √, ∴ using the panel.</li>
            <li>Tap <b>Read Aloud</b> on any answer.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
